import * as React from 'react';
import { FC } from 'react';
import dayjs from 'dayjs';
import {
  Black,
  Button,
  DatePickerItem,
  DateRangePicker,
  Gray20,
  Gray70,
  Gray100,
  IDatePickerItemProps,
  Red90,
  Skeleton,
  TextInput,
  Tooltip,
  BlackOpacity04,
  typeCompact12RegularProperties,
  typeCompact14RegularProperties,
  EmptyState,
  Badge,
} from '@traceair/tds';
import { classes, style, stylesheet } from 'typestyle';

import { SectionIcon, SectionHeader } from './components/SectionHeader';
import { InvoicePageHeader } from './components/InvoicePageHeader';
import { ItemRow } from './components/ItemRow';
import { GcpMaterialRow } from './components/GcpMaterialRow';
import { ExtraCostMenuButton } from './components/ExtraCostMenuButton';
import { OtherItemsList } from './components/OtherItemsList';
import { LeaveDialog } from './components/LeaveDialog';
import { PaymentSection } from './components/PaymentSection';
import { UploadInvoiceSection } from './components/UploadInvoiceSection';
import { TimeMaterialsCostBlock } from './components/time_materials/TimeMaterialsCostBlock';
import { useScanCosts, unpaidMissionsRange } from './hooks/useScanCosts';
import { useInvoiceForm } from './hooks/useInvoiceForm';
import { sharedInvoicingCss } from './styles/shared';
import { numericOnly, isGcpRateFullySet } from './InvoicingUtils';

// ─── Date picker cell components ─────────────────────────────────────────────

const MarkedCell: React.ComponentType<IDatePickerItemProps> = (props) => <DatePickerItem {...props} showMarker />;

const grayMarkerClass = style({ backgroundColor: `${Gray70} !important` });
const grayMarkerStyles = {
  marker: {
    inactive: grayMarkerClass,
    active: grayMarkerClass,
    rangeStart: grayMarkerClass,
    rangeEnd: grayMarkerClass,
    rangeMiddle: grayMarkerClass,
    rangeHoverStart: grayMarkerClass,
    rangeHoverEnd: grayMarkerClass,
    disabled: grayMarkerClass,
  },
};
const GrayMarkedCell: React.ComponentType<IDatePickerItemProps> = (props) => (
  <DatePickerItem {...props} showMarker customStyles={grayMarkerStyles} />
);

function makeTooltipCell(
  CellComponent: React.ComponentType<IDatePickerItemProps>,
  description: string,
): React.ComponentType<IDatePickerItemProps> {
  return function TooltipCell(props) {
    const [open, setOpen] = React.useState(false);
    return (
      <span style={{ display: 'contents' }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
        <Tooltip open={open} description={description}>
          <CellComponent {...props} />
        </Tooltip>
      </span>
    );
  };
}

const MarkedCellWithTooltip = makeTooltipCell(MarkedCell, 'Completed mission');
const GrayMarkedCellWithTooltip = makeTooltipCell(GrayMarkedCell, 'Under review');

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyDateRange: FC = () => (
  <div className={sharedInvoicingCss.emptyStateContainer}>
    <EmptyState
      description="There are no completed work items in this date range. Try adjusting the date range."
      size="s"
      className={style({ margin: '0 auto', padding: '48px 0' })}
      style="neutral"
      title="No items found"
    />
  </div>
);

// ─── Props ────────────────────────────────────────────────────────────────────

type SubmitInvoiceProps = {
  onBack?: () => void;
  onSubmit?: (data: unknown) => Promise<void> | void;
};

// ─── Component ───────────────────────────────────────────────────────────────

const SubmitInvoice: FC<SubmitInvoiceProps> = ({ onBack, onSubmit }) => {
  const { scanCosts, scanCostsFetchError, isFixedFee, dateRange, setDateRange, savedBankDetails } = useScanCosts();

  const {
    expanded,
    otherItems,
    gcpItemPrices,
    updateGcpItemPrice,
    gcpServicesTotal,
    gcpMaterialsHandlers,
    gcpMaterialsTotal,
    equipmentLeasingPrice,
    setEquipmentLeasingPrice,
    pdfFile,
    isPdfUploading,
    uploadedPdfLink,
    paymentMethod,
    setPaymentMethod,
    paymentLink,
    setPaymentLink,
    bankDetails,
    setBankDetails,
    submitAttempted,
    submittedOtherIds,
    isSubmitting,
    showLeaveDialog,
    timeMaterialsDateGroups,
    timeMaterialsDatePrices,
    setDatePrice,
    tmGcpItemPrices,
    updateTmGcpItemPrice,
    tmGcpDefaultAmountById,
    filteredCosts,
    costSections,
    billableDates,
    unprocessedDates,
    otherTotal,
    isNegativeTotal,
    isZeroTotal,
    isUploadMissing,
    isPaymentLinkMissing,
    isPaymentLinkInvalid,
    isBankDetailsMissing,
    isSingleBlankItem,
    displayTotal,
    toggleSection,
    addOtherItem,
    removeOtherItem,
    updateOtherItemLabel,
    updateOtherItemPrice,
    handlePdfChange,
    handleBack,
    handleLeaveConfirm,
    handleLeaveCancel,
    handleSubmit,
  } = useInvoiceForm({ scanCosts, dateRange, isFixedFee, savedBankDetails, onBack, onSubmit });

  const cellComponent = React.useCallback(
    (date: { format: (f: string) => string }) => {
      const key = date.format('YYYY-MM-DD');
      if (billableDates.has(key)) return MarkedCellWithTooltip;
      if (unprocessedDates.has(key)) return GrayMarkedCellWithTooltip;
      return undefined;
    },
    [billableDates, unprocessedDates],
  );

  const hasUnpaidMissions = React.useMemo(() => scanCosts !== null && scanCosts.some((c) => !c.isFailed), [scanCosts]);

  const presets = React.useMemo(
    () => [
      ...(hasUnpaidMissions
        ? [
            {
              label: 'All unpaid missions',
              value: (): [dayjs.Dayjs, dayjs.Dayjs] => unpaidMissionsRange(scanCosts!),
            },
          ]
        : []),
      { label: 'Last 7 days', value: (): [dayjs.Dayjs, dayjs.Dayjs] => [dayjs().subtract(6, 'day'), dayjs()] },
      { label: 'Last 14 days', value: (): [dayjs.Dayjs, dayjs.Dayjs] => [dayjs().subtract(13, 'day'), dayjs()] },
      { label: 'Last 30 days', value: (): [dayjs.Dayjs, dayjs.Dayjs] => [dayjs().subtract(29, 'day'), dayjs()] },
    ],
    [scanCosts, hasUnpaidMissions],
  );

  const css = { ...sharedInvoicingCss, ...localCss };

  return (
    <>
      <div className={css.page}>
        {/* Header */}
        <InvoicePageHeader
          title="Submit Invoice"
          onBack={handleBack}
          badge={<Badge className={localCss.badgeDraft} size="l" label="Draft" />}
        />

        {/* Period */}
        <div className={css.periodSection}>
          <div className={css.periodLabel}>INVOICE PERIOD</div>
          <DateRangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            placeholder={['Start date', 'End date']}
            className={localCss.datePickerInput}
            cellComponent={cellComponent}
            allowClear={false}
            presets={presets}
          />
        </div>
        <p className={localCss.description}>Review all completed missions and verify their costs</p>

        {/* Cost card */}
        <div className={css.costCard}>
          {renderCostCard()}

          {/* Other section */}
          <div>
            <SectionHeader
              section={{ id: 'other-costs', label: 'Other', items: [], total: otherTotal }}
              isExpanded={expanded['other-costs'] ?? true}
              onToggle={() => toggleSection('other-costs')}
              subtitle="Additional expenses"
              className={classes(
                css.otherSectionHeader,
                css.otherSectionHeaderEditable,
                (expanded['other-costs'] ?? true) && css.otherSectionHeaderExpanded,
              )}
            />
            {(expanded['other-costs'] ?? true) && (
              <OtherItemsList
                items={otherItems}
                onRemove={removeOtherItem}
                onLabelChange={updateOtherItemLabel}
                onPriceChange={updateOtherItemPrice}
                onAdd={addOtherItem}
                isLabelError={(item) => submittedOtherIds.has(item.id) && !item.label.trim() && !isSingleBlankItem}
              />
            )}
          </div>

          <div className={css.cardDivider} />
          <div className={classes(css.customFieldRow)}>
            <div className={css.customFieldInfo}>
              <span className={css.customFieldLabel}>Equipment leasing</span>
              <span className={css.customFieldDesc}>TraceAir equipment rental expenses</span>
            </div>
            <div className={css.priceInputContainer}>
              <TextInput
                leftCell={equipmentLeasingPrice ? '$' : undefined}
                value={equipmentLeasingPrice}
                placeholder="Price"
                onChange={(value) => setEquipmentLeasingPrice(numericOnly(value))}
              />
            </div>
          </div>

          <div className={css.cardDivider} />
          <div className={css.totalRow}>
            <span className={css.totalLabel}>Total</span>
            <span
              className={css.totalAmount}
              style={isNegativeTotal || (submitAttempted && isZeroTotal) ? { color: Red90 } : undefined}
            >
              {displayTotal}
            </span>
          </div>
          {isNegativeTotal ? (
            <p className={localCss.totalError}>The total cannot be negative</p>
          ) : (
            submitAttempted && isZeroTotal && <p className={localCss.totalError}>The total cannot be zero</p>
          )}
        </div>

        {/* Upload Invoice */}
        <div className={css.outerSection}>
          <div className={css.outerSectionHead}>
            <div className={css.outerSectionTitle}>Upload Invoice</div>
            <div className={css.outerSectionSubtitle}>Must match the items listed above</div>
          </div>
          <UploadInvoiceSection
            file={pdfFile}
            isUploading={isPdfUploading}
            uploadedLink={uploadedPdfLink}
            isError={submitAttempted && isUploadMissing}
            onFileChange={(file) => void handlePdfChange(file)}
          />
        </div>

        {/* Payment info */}
        <div className={css.outerSection}>
          <div className={css.outerSectionHead}>
            <div className={css.outerSectionTitle}>Payment info</div>
            <div className={css.outerSectionSubtitle}>Select the payment method you prefer</div>
          </div>
          <PaymentSection
            method={paymentMethod}
            paymentLink={paymentLink}
            bankDetails={bankDetails}
            onMethodChange={setPaymentMethod}
            onLinkChange={setPaymentLink}
            onBankChange={setBankDetails}
            isLinkError={submitAttempted && (isPaymentLinkMissing || isPaymentLinkInvalid)}
            isBankError={submitAttempted && isBankDetailsMissing}
            linkErrorMessage={
              submitAttempted
                ? isPaymentLinkMissing
                  ? 'This field is required'
                  : isPaymentLinkInvalid
                    ? 'Link is not valid'
                    : undefined
                : undefined
            }
            bankErrorMessage={submitAttempted && isBankDetailsMissing ? 'This field is required' : undefined}
          />
        </div>

        {/* Footer */}
        <div className={css.footer}>
          <div className={css.footerNoteWrap}>
            <p className={css.footerNote}>
              Once submitted, you can&apos;t edit this invoice. Reviewed by Operations Manager before payment.
            </p>
          </div>
          <Button
            style="primary"
            text={isSubmitting ? 'Submitting…' : 'Submit Invoice'}
            disabled={isSubmitting}
            onClick={handleSubmit}
          />
        </div>
      </div>

      <LeaveDialog isOpen={showLeaveDialog} onCancel={handleLeaveCancel} onConfirm={handleLeaveConfirm} />
    </>
  );

  function renderCostCard() {
    if (scanCostsFetchError) {
      return <p className={localCss.costCardMessage}>Failed to load scan costs. Please refresh and try again.</p>;
    }

    if (scanCosts === null) {
      return isFixedFee ? renderFixedFeeSkeletons() : renderTimeMaterialsSkeletons();
    }

    if (isFixedFee) {
      if (filteredCosts !== null && filteredCosts.length === 0) return <EmptyDateRange />;
      return renderFixedFeeSections();
    }

    // Time & Materials mode
    if (!filteredCosts || filteredCosts.length === 0) return <EmptyDateRange />;
    return (
      <TimeMaterialsCostBlock
        timeMaterialsDateGroups={timeMaterialsDateGroups}
        expanded={expanded}
        timeMaterialsDatePrices={timeMaterialsDatePrices}
        setDatePrice={setDatePrice}
        gcpItemPrices={tmGcpItemPrices}
        updateGcpItemPrice={updateTmGcpItemPrice}
        gcpDefaultAmountById={tmGcpDefaultAmountById}
        gcpMaterials={gcpMaterialsHandlers}
        toggleSection={toggleSection}
      />
    );
  }

  function renderFixedFeeSections() {
    return costSections.map((section) => {
      const isGcpServices = section.id === 'gcp_services';
      const isExpanded = expanded[section.id] ?? true;
      const displaySection = isGcpServices ? { ...section, total: gcpServicesTotal + gcpMaterialsTotal } : section;
      return (
        <div key={section.id}>
          <SectionHeader section={displaySection} isExpanded={isExpanded} onToggle={() => toggleSection(section.id)} />
          {isExpanded && (
            <>
              {section.items.length === 0 ? (
                <p className={css.emptySectionMsg}>No items for this period</p>
              ) : (
                <div className={css.sectionItemsContainer}>
                  {section.items.map((item) => {
                    if (!isGcpServices) return <ItemRow key={item.id} item={item} />;
                    const gcpKey = item.gcpServiceId ?? item.id;
                    const entries = gcpMaterialsHandlers.entries[gcpKey] ?? [];
                    const hasMaterials = entries.some((e) => e.type === 'materials');
                    const hasMileage = entries.some((e) => e.type === 'mileage');
                    // Once the consultant's hourly rate and confirmed hours are both known, the
                    // price is fully auto-calculated and shown read-only; otherwise it's editable.
                    const isRateFullySet = isGcpRateFullySet(
                      item.consultantGcpServiceHourlyRate,
                      item.consultantGcpServiceTimeInHours,
                    );
                    return (
                      <div key={item.id}>
                        <ItemRow
                          item={item}
                          amountInput={
                            item.status !== 'failed' && !isRateFullySet
                              ? gcpItemPrices[item.id] ?? String(item.amount ?? 0)
                              : undefined
                          }
                          onAmountChange={(val) => updateGcpItemPrice(item.id, val)}
                          actionNode={
                            item.status !== 'failed' ? (
                              <ExtraCostMenuButton
                                hasMaterials={hasMaterials}
                                hasMileage={hasMileage}
                                onSelect={(type) => gcpMaterialsHandlers.onAdd(gcpKey, type)}
                              />
                            ) : undefined
                          }
                        />
                        {entries.map((entry) => (
                          <GcpMaterialRow
                            key={entry.id}
                            label={entry.type === 'materials' ? 'GCP Materials' : 'Mileage'}
                            amount={entry.amount}
                            onAmountChange={(val) => gcpMaterialsHandlers.onAmountChange(gcpKey, entry.id, val)}
                            onRemove={() => gcpMaterialsHandlers.onRemove(gcpKey, entry.id)}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      );
    });
  }

  function renderTimeMaterialsSkeletons() {
    const subRows = [
      { id: 'scan', title: 'Scans, panoramas price', input: true },
      { id: 'gcp_services', title: 'GCP services', input: false },
      { id: 'mileage', title: 'Mileage', input: true },
    ] as const;

    return (
      <>
        {[{ expanded: true }, { expanded: false }].map((group, i) => (
          <div key={i}>
            <div className={css.sectionHeader} style={{ cursor: 'default', pointerEvents: 'none' }}>
              <span style={{ flex: 1 }}>
                <Skeleton width={72} height={14} />
              </span>
              <Skeleton width={44} height={14} />
            </div>
            {group.expanded && (
              <>
                {subRows.map((sub) => (
                  <div
                    key={sub.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 16px',
                      borderBottom: `1px solid ${Gray20}`,
                      cursor: 'default',
                      pointerEvents: 'none',
                    }}
                  >
                    <div className={css.sectionTitleGroup}>
                      <span className={css.sectionTitle}>{sub.title}</span>
                    </div>
                    {sub.input ? <Skeleton width={90} height={38} /> : <Skeleton width={44} height={14} />}
                  </div>
                ))}
              </>
            )}
          </div>
        ))}
      </>
    );
  }

  function renderFixedFeeSkeletons() {
    const categories = [
      { id: 'scan', label: 'Scans' },
      { id: 'pano', label: 'Additional Panoramas' },
      { id: 'lidar', label: 'LiDAR' },
      { id: 'gcp_services', label: 'GCP services' },
    ] as const;

    return (
      <>
        {categories.map((cat) => (
          <div key={cat.id} className={css.sectionHeader} style={{ cursor: 'default', pointerEvents: 'none' }}>
            <span className={css.sectionChevron}>
              <SectionIcon category={cat.id} isExpanded={false} />
            </span>
            <div className={css.sectionTitleGroup}>
              <span className={css.sectionTitle}>{cat.label}</span>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                <Skeleton width={52} height={12} />
                <Skeleton width={40} height={12} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Skeleton width={30} height={20} />
              <Skeleton width={30} height={20} />
            </div>
          </div>
        ))}
      </>
    );
  }
};

// ─── Local-only stylesheet ────────────────────────────────────────────────────

const localCss = stylesheet({
  badgeDraft: {
    color: Gray100,
    backgroundColor: BlackOpacity04,
    borderRadius: 10,
    backdropFilter: 'blur(12.5px)',
  },
  datePickerInput: {
    width: 230,
  },
  description: {
    margin: 0,
    ...typeCompact14RegularProperties,
    color: Black,
  },
  costCardMessage: {
    margin: 0,
    ...typeCompact14RegularProperties,
    padding: '24px 16px',
    color: Gray100,
    textAlign: 'center' as const,
  },
  totalError: {
    ...typeCompact12RegularProperties,
    padding: '0 16px 12px',
    color: Red90,
    textAlign: 'right' as const,
    margin: 0,
  },
});

export default SubmitInvoice;
